/**
 * Configurator interaction tracking (GA4 via the server-side GTM container).
 *
 * Pushes seven `configurator_*` events onto `window.dataLayer`, which the Tagging
 * plugin's container (GTM-5QQ28MK) forwards to GA4:
 *
 *   configurator_open          panel opened
 *   configurator_field_change  a field was changed (text/number debounced)
 *   configurator_info_open     an "i" help icon was opened
 *   configurator_complete      config became valid (edge only, once)
 *   configurator_error         an out-of-range dimension was entered
 *   configurator_submit        added to cart
 *   configurator_abandon       left without adding to cart
 *
 * WHY IT ONLY READS THE DOM
 * The pricing/add-to-cart logic is a ~500-line inline script in
 * template-parts/product-configurator2.php, guarded by six e2e specs. A syntax
 * error there breaks checkout, so analytics must not be able to touch it.
 * Everything needed is already legible in the markup:
 *
 *   price updated   -> childList mutation on .configurator__totals
 *   config complete -> button[name=add-to-cart] loses .disabled
 *                      (toggleAddToCartButtons() is its ONLY writer and is called
 *                       with exactly `lastLabel == 'Totaal'`, so this is an exact
 *                       mirror of the server's completeness verdict)
 *   size error      -> #min-max-popup gains .show
 *   help opened     -> click on .configurator__tooltip-icon while its
 *                      .configurator__tooltip-text is not yet .active
 *
 * That DOM contract is implicit, so it is pinned by tests/e2e/configurator-tracking.spec.ts.
 *
 * HOW TO READ THE DATA
 * The headline metric is "sessions with configurator_abandon and no
 * configurator_submit", broken down by step_title / field_name. Abandonment is
 * reported per page-hide (see flush() below), so counting distinct sessions —
 * not events — is what makes it accurate.
 */

const EVENT_PREFIX = 'configurator_';

// Text/number fields fire once after typing settles instead of per keystroke.
const TYPING_DEBOUNCE_MS = 700;

// Never tracked at all: form plumbing, and anything that could carry personal data.
//
// This list is the PRIMARY defence, not a backstop. content-configurator.php is
// included inside <form class="cart"> (simple.php:86), so the shipping-calculator
// and email-popup inputs are physically nested in the form and their events bubble
// to the delegated listeners below — even though their form="" attributes re-parent
// them for submission and keep them out of form.elements.
//
// Postcode is deliberately never sent: a 6-character NL postcode plus a house
// number is a full address, which is personal data under GDPR and against Google's
// policy on sending identifying data to GA. GA4 already derives city/region from IP.
const IGNORED_FIELDS = [
    'product_id',
    'quantity',
    'add-to-cart',
    'update_cart_item_key',
    'original_shipping_method',
];

const IGNORED_PATTERNS = [
    /^calc_shipping_/,      // postcode / housenumber / city / country / state
    /email/i,
    /telefoon|phone/i,
    /naam|name$/i,
    /^woocommerce-/,        // nonces
    /nonce/i,
];

// Fields whose *value* may be sent. Exact matches only, so a newly added field is
// tracked by name/label but has its value redacted until it is listed here —
// default-deny. Mirrors the 'name' keys in hordeur-configuration.php,
// cassettes-configuration.php and configurator-common.php.
const VALUE_ALLOWED = [
    'breedte', 'breedte_cassette', 'breedte_cassette_dubbel', 'breedte_profiel_input',
    'eigen_kleur', 'framekleur', 'gaaskleur', 'handgreep', 'height_totale_situatie',
    'hoogte', 'hoogte_cassette', 'hoogte_profiel_input', 'korting', 'magneetsluiting',
    'op_de_dag', 'powertape', 'schuin', 'standaard_of_eigen_kleur', 'type_eigen_kleur',
    'type_hordeur', 'type_op_de_dag', 'vervangen',
    'shipping_method',
];

const REDACTED = '(redacted)';

// Labels for inputs that sit outside a .configurator__input-group wrapper.
const EXTRA_LABELS = { shipping_method: 'Verzendmethode' };

/** shipping_method[0] -> shipping_method */
function normaliseName(name) {
    return name.replace(/\[\d*\]$/, '');
}

// "Breedte (400 tot 3000)" -> "Breedte". Mirrors the $rangeLabel formats built in
// product-configurator2.php: "(%d tot %d)", "(Min. %d)", "(Max. %d)".
const RANGE_SUFFIX = /\s*\((?:Min\.|Max\.)?\s*\d+(?:\s+tot\s+\d+)?\)\s*$/;

function isTrackableField(rawName) {
    if (!rawName) return false;
    const name = normaliseName(rawName);
    if (IGNORED_FIELDS.indexOf(name) !== -1) return false;
    return !IGNORED_PATTERNS.some((re) => re.test(name));
}

function text(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

function toNumber(raw) {
    // "€ 1.234,56" -> 1234.56 (nl-NL). Mirrors parseEuro() in tests/e2e/helpers.ts.
    const cleaned = String(raw).replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.');
    const value = parseFloat(cleaned);
    return isNaN(value) ? null : value;
}

/** Strip empties and clamp to GA4's limits (string values max 100 chars). */
function clean(params) {
    const out = {};
    Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === null || value === undefined || value === '') return;
        out[key] = typeof value === 'number' ? value : String(value).slice(0, 100);
    });
    return out;
}

/**
 * GTM merges dataLayer objects recursively, so without an explicit reset a param
 * from an earlier event (e.g. error_type) survives into the next one and a shared
 * GA4 tag sends stale values. The Tagging plugin uses the same `ecommerce: null`
 * idiom for the same reason. Nesting under one key also means we can never
 * collide with an unrelated GTM variable.
 */
function push(eventName, params) {
    if (window.__cfgTrackingEnabled === false) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ configurator: null });
    window.dataLayer.push({ event: EVENT_PREFIX + eventName, configurator: clean(params) });
}

function init() {
    const form = document.querySelector('form.cart');
    const panel = document.querySelector('.js-product-config');
    if (!form || !panel) return;

    const state = {
        opened: false,
        replaying: false,
        submitted: false,
        complete: false,
        changes: 0,
        changesAtLastFlush: -1,
        startedAt: null,
        lastField: null,      // {name, label}
        furthestStep: null,   // {number, title}
        price: null,
        lastValue: {},        // field name -> last emitted value, kills duplicate pushes
        perField: {},         // field name -> how many times changed
        infoOpens: 0,         // how many times a help icon was opened
        perFieldInfo: {},     // field name -> how many times its help was opened
        timers: {},
    };

    /* ---------------------------------------------------------------- context */

    const productContext = () => {
        // window.viewItem is set by the Tagging plugin on every product page.
        const item = (window.viewItem && window.viewItem.items && window.viewItem.items[0]) || {};
        return {
            configurator_type: panel.getAttribute('data-configurator-type') || 'hordeur',
            product_id: panel.getAttribute('data-product-id')
                || (form.querySelector('input[name="product_id"]') || {}).value,
            product_name: item.item_name,
        };
    };

    /* ------------------------------------------------------------- DOM digging */

    const wrapperOf = (el) => el.closest('.configurator__input-group');

    /**
     * The visible label of a field wrapper, minus its range hint. The tooltip is
     * stripped because the pre-configured ("mock") variant in
     * product-configurator2.php nests .configurator__tooltip INSIDE
     * .configurator__label — without this the icon's "i" and the entire help text
     * would land in field_label.
     */
    function labelText(wrapper) {
        const label = wrapper.querySelector('.configurator__label');
        if (!label) return '';
        const clone = label.cloneNode(true);
        clone.querySelectorAll('.configurator__tooltip').forEach((n) => n.remove());
        return text(clone).replace(RANGE_SUFFIX, '');
    }

    function fieldLabel(el) {
        const wrapper = wrapperOf(el);
        if (!wrapper) return EXTRA_LABELS[normaliseName(el.getAttribute('name') || '')] || '';
        return labelText(wrapper);
    }

    function optionLabel(el) {
        if (el.tagName === 'SELECT') {
            return el.selectedIndex >= 0 ? text(el.options[el.selectedIndex]) : '';
        }
        if (el.type !== 'radio' && el.type !== 'checkbox') return '';

        const label = el.id && document.querySelector('label[for="' + CSS.escape(el.id) + '"]');
        if (!label) return '';

        // 'radio' nests .configurator__label-text inside the label; 'blockRadio'
        // puts it *after* the label as a sibling.
        const inside = label.querySelector('.configurator__label-text');
        if (inside) return text(inside);

        const sibling = label.nextElementSibling;
        if (sibling && sibling.classList.contains('configurator__label-text')) return text(sibling);

        // Fall back to the label's own text, minus the price hint. Note
        // .configurator__radio-info is used for BOTH the hint <span> and the
        // radioImagesEnd <img>, so remove every match.
        const clone = label.cloneNode(true);
        clone.querySelectorAll('.configurator__radio-info').forEach((n) => n.remove());
        return text(clone);
    }

    function stepOf(el) {
        const step = el.closest('.configurator__step');
        if (!step) return {};
        const title = text(step.querySelector('h2'));
        const superTitle = text(step.querySelector('.configurator__supertitle'));
        const match = superTitle.match(/\d+/);
        // #shipping-configuration is a .configurator__step with no head, so fall
        // back to its position. Resolved per call: that element is replaced
        // wholesale on every price refresh.
        const steps = Array.prototype.slice.call(form.querySelectorAll('.configurator__step'));
        return {
            step_number: match ? parseInt(match[0], 10) : steps.indexOf(step) + 1,
            step_title: title,
        };
    }

    /**
     * Conditional logic hides a field by setting display:none AND removing its
     * `name` attribute. Test the attribute, not offsetParent — the whole panel is
     * display:none until it is opened, so offsetParent is null for everything.
     */
    function progress() {
        const seen = {};
        let total = 0;
        let completed = 0;

        // Only `required` fields count, which naturally excludes the shipping-method
        // radios; `name` is still present only while conditional logic keeps the
        // field in play, so hidden branches drop out of both numbers.
        Array.prototype.forEach.call(form.elements, (el) => {
            const name = el.getAttribute('name');
            if (!name || !isTrackableField(name) || !el.required) return;
            if (seen[name]) return;
            seen[name] = true;
            total++;

            if (el.type === 'radio') {
                if (form.querySelector('input[name="' + CSS.escape(name) + '"]:checked')) completed++;
            } else if (el.value !== '') {
                completed++;
            }
        });

        return { fields_total: total, fields_completed: completed };
    }

    const addToCartButton = () => form.querySelector('button[name="add-to-cart"]');

    function isComplete() {
        const button = addToCartButton();
        return !!button && !button.classList.contains('disabled');
    }

    function currentPrice() {
        const totals = document.querySelector('.configurator__totals');
        if (!totals) return null;
        const rows = totals.querySelectorAll('tr');
        for (let i = rows.length - 1; i >= 0; i--) {
            const head = rows[i].querySelector('th');
            if (head && text(head) === 'Totaal') {
                const cells = rows[i].querySelectorAll('td');
                if (cells.length) return toNumber(text(cells[cells.length - 1]));
            }
        }
        return null;
    }

    const seconds = () => (state.startedAt ? Math.round((Date.now() - state.startedAt) / 1000) : 0);

    /* ------------------------------------------------------------------ events */

    function trackOpen() {
        if (state.opened) return;
        state.opened = true;
        state.startedAt = state.startedAt || Date.now();
        push('open', Object.assign({}, productContext(), progress()));
    }

    function trackFieldChange(el) {
        const rawName = el.getAttribute('name');
        if (!isTrackableField(rawName)) return;
        const name = normaliseName(rawName);

        const rawValue = el.type === 'checkbox' ? String(el.checked) : el.value;
        if (rawValue === '') return;

        // Conditional logic and the shipping re-render both re-emit values that
        // did not actually change.
        if (state.lastValue[name] === rawValue) return;
        state.lastValue[name] = rawValue;

        // Values being replayed from the cookie are prior state, not a new
        // interaction — record them (so a later genuine pick still dedupes
        // correctly) but do not report them. See the replay guard in init().
        if (state.replaying) return;

        state.startedAt = state.startedAt || Date.now();
        state.changes++;
        state.perField[name] = (state.perField[name] || 0) + 1;

        const step = stepOf(el);
        state.lastField = { name, label: fieldLabel(el) };
        if (step.step_number && (!state.furthestStep || step.step_number > state.furthestStep.step_number)) {
            state.furthestStep = step;
        }

        const allowed = VALUE_ALLOWED.indexOf(name) !== -1;
        push('field_change', Object.assign({}, productContext(), progress(), step, {
            field_name: name,
            field_label: state.lastField.label,
            field_value: allowed ? rawValue : REDACTED,
            field_value_label: allowed ? optionLabel(el) : '',
            changes_count: state.changes,
            field_changes_count: state.perField[name],
        }));
    }

    /**
     * A help ("i") icon was opened. Reported on the OPEN edge only: the icon is a
     * toggle, so counting every click would make "how often did visitors need this
     * explanation" twice the real number for anyone who closes it again.
     *
     * The three tooltip variants in product-configurator2.php all sit in the label
     * wrapper of the field they explain (the pre-configured variant in a plain
     * .configurator__col rather than an .configurator__input-group), so the field
     * is identified from that wrapper's named input.
     *
     * Deliberately does NOT touch state.changes / state.lastField: opening help is
     * not a configuration change, and folding it in would inflate changes_count and
     * make the abandon event blame a field the visitor never actually filled in.
     */
    function trackInfoOpen(icon) {
        const wrapper = icon.closest('.configurator__input-group') || icon.closest('.configurator__col');
        if (!wrapper) return;

        const input = wrapper.querySelector('[name]');
        const rawName = input && input.getAttribute('name');
        if (!isTrackableField(rawName)) return;
        const name = normaliseName(rawName);

        state.startedAt = state.startedAt || Date.now();
        state.infoOpens++;
        state.perFieldInfo[name] = (state.perFieldInfo[name] || 0) + 1;

        push('info_open', Object.assign({}, productContext(), progress(), stepOf(icon), {
            field_name: name,
            field_label: labelText(wrapper),
            info_opens_count: state.infoOpens,
            field_info_opens_count: state.perFieldInfo[name],
            time_in_config: seconds(),
        }));
    }

    function trackComplete() {
        push('complete', Object.assign({}, productContext(), progress(), {
            config_value: state.price,
            currency: 'EUR',
            time_in_config: seconds(),
            changes_count: state.changes,
        }));
    }

    function trackError(el) {
        const min = parseInt(el.getAttribute('min'), 10);
        const max = parseInt(el.getAttribute('max'), 10);
        const value = parseInt(el.value, 10);
        if (isNaN(value)) return;

        let detail = '';
        if (!isNaN(min) && value < min) detail = 'too_small';
        else if (!isNaN(max) && value > max) detail = 'too_large';
        if (!detail) return;

        push('error', Object.assign({}, productContext(), stepOf(el), {
            error_type: 'size_out_of_range',
            error_detail: detail,
            field_name: el.getAttribute('name'),
            field_label: fieldLabel(el),
            field_value: el.value,
        }));
    }

    function trackSubmit() {
        if (state.submitted) return;
        state.submitted = true;
        push('submit', Object.assign({}, productContext(), progress(), {
            config_value: state.price,
            currency: 'EUR',
            time_in_config: seconds(),
            changes_count: state.changes,
        }));
    }

    /**
     * Abandonment. No beforeunload: it disqualifies the page from bfcache and is
     * ignored on mobile. visibilitychange->hidden is the last reliable callback,
     * with pagehide covering the desktop-Safari gap.
     *
     * A mobile user switching apps looks identical to a user leaving, so rather
     * than guess we report each hide and let GA4 answer the real question
     * ("sessions with an abandon and no submit"). The changesAtLastFlush guard is
     * what stops an idle switch-away/switch-back cycle reporting twice.
     */
    function flush(reason) {
        if (state.submitted) return;                          // add-to-cart also hides the page
        if (state.changes === 0) return;                      // never engaged
        if (state.changes === state.changesAtLastFlush) return; // nothing new since last report
        state.changesAtLastFlush = state.changes;

        const step = state.furthestStep || {};
        push('abandon', Object.assign({}, productContext(), progress(), {
            config_complete: String(state.complete),
            config_value: state.price,
            currency: 'EUR',
            field_name: state.lastField && state.lastField.name,
            field_label: state.lastField && state.lastField.label,
            step_number: step.step_number,
            step_title: step.step_title,
            changes_count: state.changes,
            time_in_config: seconds(),
            abandon_reason: reason,
        }));
    }

    /* --------------------------------------------------------------- listeners */

    // Delegated on form.cart, so the wholesale replacement of #shipping-configuration
    // on every price refresh cannot orphan a listener. Note this still receives
    // events from the shipping-calculator and email-popup inputs, which are nested
    // inside the form even though their form="" attributes re-parent them for
    // submission — isTrackableField() is what keeps those out.
    form.addEventListener('change', (e) => {
        const el = e.target;
        if (!el.name) return;
        // A text/number field also fires `change` on blur; take it now and cancel
        // the pending debounce so it is reported once, not twice.
        window.clearTimeout(state.timers[el.name]);
        trackFieldChange(el);
    });

    form.addEventListener('input', (e) => {
        const el = e.target;
        if (!el.name || (el.type !== 'text' && el.type !== 'number')) return;
        window.clearTimeout(state.timers[el.name]);
        state.timers[el.name] = window.setTimeout(() => trackFieldChange(el), TYPING_DEBOUNCE_MS);
    });

    form.addEventListener('focusout', (e) => {
        const el = e.target;
        if (!el.name || (el.type !== 'text' && el.type !== 'number')) return;
        window.clearTimeout(state.timers[el.name]);
        trackFieldChange(el);
        if (el.type === 'number') trackError(el);
    }, true);

    // Capture phase, so it runs before configurator.js's own submit handler.
    form.addEventListener('submit', trackSubmit, true);
    form.addEventListener('click', (e) => {
        const button = e.target.closest && e.target.closest('button[name="add-to-cart"]');
        if (button && !button.classList.contains('disabled')) trackSubmit();
    }, true);

    /**
     * Opening the panel makes configurator.js replay every stored radio with
     * jQuery .trigger('click'), which would otherwise fabricate a burst of field
     * changes on every reopen and silently poison the dataset.
     *
     * e.isTrusted does NOT filter these: .trigger('click') routes to native
     * el.click(), whose *click* event is untrusted, but the `change` the browser
     * then fires as part of the radio's activation behaviour is UA-generated and
     * therefore trusted. So suppress by window instead. This handler is on
     * document in the CAPTURE phase, so it runs before configurator.js's own
     * target-phase handler; the replay inside that handler is synchronous, so a
     * macrotask is enough to re-arm.
     */
    document.addEventListener('click', (e) => {
        if (!e.target.closest) return;
        if (e.target.closest('.js-config-button')) {
            state.replaying = true;
            window.setTimeout(() => { state.replaying = false; }, 0);
            trackOpen();
        }
    }, true);

    document.addEventListener('click', (e) => {
        if (!e.target.closest) return;
        if (e.target.closest('.js-config-back')) flush('closed_panel');
    });

    /**
     * Help icons. CAPTURE phase for two reasons: the inline handler in
     * product-configurator2.php calls e.stopPropagation(), so a bubbling listener
     * on document would never run; and running first means .active still reflects
     * the state BEFORE the toggle, which is what tells an opening click from a
     * closing one.
     *
     * On document rather than the form because the icons are inside
     * .configurator__label-wrapper blocks that conditional logic and the shipping
     * re-render replace wholesale.
     */
    document.addEventListener('click', (e) => {
        if (!e.target.closest) return;
        const icon = e.target.closest('.configurator__tooltip-icon');
        if (!icon) return;
        const help = icon.nextElementSibling;
        if (help && help.classList.contains('active')) return;   // this click closes it
        trackInfoOpen(icon);
    }, true);

    window.addEventListener('pagehide', () => flush('pagehide'));
    window.addEventListener('pageshow', () => { state.changesAtLastFlush = -1; });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush('hidden');
        else state.changesAtLastFlush = -1;
    });

    // The price table is rewritten in one innerHTML assignment per refresh, and
    // the add-to-cart button's .disabled class is flipped in the same pass.
    const totals = document.querySelector('.configurator__totals');
    if (totals) {
        new MutationObserver(() => {
            state.price = currentPrice();
            const complete = isComplete();
            if (complete && !state.complete) {
                state.complete = true;
                trackComplete();          // edge only: not on every refresh while complete
            } else if (!complete) {
                state.complete = false;
            }
        }).observe(totals, { childList: true, subtree: true });
    }

    if (panel.offsetParent !== null) trackOpen();   // already open (deep link / edit mode)
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();   // combined/deferred JS can land after DOMContentLoaded
}
